// src/utils/security/accessControl.js

/*
 * ShiftBridge Access-Control Compatibility Layer
 *
 * CANONICAL AUTHORITY:
 *
 *   src/security/permissions.js
 *
 * This module preserves the older accessControl API used by existing
 * screens, but it must NOT maintain its own role, tenant, facility,
 * or permission rules.
 */

import {
  PERMISSIONS,
  normalizeId,
  isOwnerAdmin as canonicalIsOwnerAdmin,
  isAgencyAdmin as canonicalIsAgencyAdmin,
  isPlatformAdmin as canonicalIsPlatformAdmin,
  isFacilityClient as canonicalIsFacilityClient,
  isWorker as canonicalIsWorker,
  getTenantId as canonicalGetTenantId,
  getFacilityIds as canonicalGetFacilityIds,
  canAccessTenant as canonicalCanAccessTenant,
  canAccessFacility as canonicalCanAccessFacility,
  hasPermission,
} from "../../security/permissions";

/* =========================================================
   NORMALIZATION
   ========================================================= */

export { normalizeId };

/* =========================================================
   ROLE HELPERS
   ========================================================= */

export function isOwnerAdmin(profile = {}) {
  return canonicalIsOwnerAdmin(profile);
}

export function isAgencyAdmin(profile = {}) {
  return canonicalIsAgencyAdmin(profile);
}

/*
 * Generic enterprise/platform administrator family.
 *
 * Includes:
 * - owner_admin
 * - operations_admin
 * - finance_admin
 * - agency_admin
 *
 * Individual capabilities must still be checked
 * through hasPermission().
 */
export function isPlatformAdmin(profile = {}) {
  return canonicalIsPlatformAdmin(profile);
}

export function isFacilityClient(profile = {}) {
  return canonicalIsFacilityClient(profile);
}

export function isWorker(profile = {}) {
  return canonicalIsWorker(profile);
}

/* =========================================================
   TENANT / FACILITY SCOPE
   ========================================================= */

export function getTenantId(profile = {}) {
  return canonicalGetTenantId(profile);
}

export function getFacilityIds(profile = {}) {
  return canonicalGetFacilityIds(profile);
}

export function canAccessTenant(
  profile = {},
  tenantId = ""
) {
  return canonicalCanAccessTenant(
    profile,
    tenantId
  );
}

export function canAccessFacility(
  profile = {},
  facilityId = "",
  tenantId = ""
) {
  return canonicalCanAccessFacility(
    profile,
    facilityId,
    tenantId
  );
}

/* =========================================================
   FINANCIAL VISIBILITY
   ========================================================= */

export function canViewPayroll(profile = {}) {
  return (
    hasPermission(
      profile,
      PERMISSIONS.CAN_VIEW_PAYROLL
    ) ||
    hasPermission(
      profile,
      PERMISSIONS.CAN_RUN_PAYROLL
    ) ||
    hasPermission(
      profile,
      PERMISSIONS.CAN_GENERATE_PAYROLL
    ) ||
    hasPermission(
      profile,
      PERMISSIONS.CAN_MANAGE_PAYROLL
    )
  );
}

export function canViewMargins(profile = {}) {
  return hasPermission(
    profile,
    PERMISSIONS.CAN_VIEW_FINANCIAL_PROJECTIONS
  );
}

export function canViewBillRates(profile = {}) {
  return hasPermission(
    profile,
    PERMISSIONS.CAN_VIEW_RATES
  );
}

/* =========================================================
   FACILITY PULSE
   ========================================================= */

export function canViewFacilityPulse(
  profile = {},
  facility = {}
) {
  const tenantId = normalizeId(
    facility?.tenantId || ""
  );

  const facilityId = normalizeId(
    facility?.id ||
      facility?.facilityId ||
      ""
  );

  if (!facilityId) {
    return false;
  }

  if (
    !canAccessFacility(
      profile,
      facilityId,
      tenantId
    )
  ) {
    return false;
  }

  return hasPermission(
    profile,
    PERMISSIONS.CAN_VIEW_FACILITY_PULSE
  );
}

/* =========================================================
   FACILITY VISIBILITY SETTINGS
   ========================================================= */

/*
 * These helpers preserve facility-configured visibility settings.
 * The user's basic facility authorization still comes from the
 * canonical permission/scope engine.
 */

export function canViewWorkerOutsideFacility(
  profile = {},
  facility = {}
) {
  if (isOwnerAdmin(profile)) {
    return true;
  }

  return (
    facility?.visibility
      ?.facilityCanSeeWorkersOutsideFacility ===
    true
  );
}

export function canFacilitySeePayroll(
  profile = {},
  facility = {}
) {
  if (isOwnerAdmin(profile)) {
    return true;
  }

  if (
    !hasPermission(
      profile,
      PERMISSIONS.CAN_VIEW_PAYROLL
    ) &&
    !hasPermission(
      profile,
      PERMISSIONS.CAN_RUN_PAYROLL
    )
  ) {
    return false;
  }

  return (
    facility?.visibility
      ?.facilityCanSeePayroll === true
  );
}

export function canFacilitySeeMargins(
  profile = {},
  facility = {}
) {
  if (isOwnerAdmin(profile)) {
    return true;
  }

  if (
    !hasPermission(
      profile,
      PERMISSIONS.CAN_VIEW_FINANCIAL_PROJECTIONS
    )
  ) {
    return false;
  }

  return (
    facility?.visibility
      ?.facilityCanSeeMargin === true
  );
}